import React, { useState } from "react";
import { Form, Input, Upload, Rate, Button, Card, Space, Tabs, Modal } from "antd";
import { PlusOutlined, DeleteOutlined, UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ISetupWebsiteFormProps } from "../../types/website.type";
import { useUploadPhotoApi } from "../../services/apis/common.api";
import { useRootSelector } from "../../stores/reducers/root";

const { TextArea } = Input;

const CustomerReviews: React.FC<ISetupWebsiteFormProps> = ({ form }) => {
  const { t } = useTranslation();
  const { mutate: uploadPhoto, isLoading: uploading } = useUploadPhotoApi();
  const { albums: selectedAlbums } = useRootSelector((state) => state.website);
  const [isAlbumModalOpen, setIsAlbumModalOpen] = useState(false);
  const [currentReviewIndex, setCurrentReviewIndex] = useState<number | null>(null);
  const [currentField, setCurrentField] = useState<"customer_reviews" | "customer_reviews_en" | null>(null);

  // Use Form.useWatch to get real-time form values
  const rawCustomerReviews = Form.useWatch("customer_reviews", form) || [];
  const rawCustomerReviewsEn = Form.useWatch("customer_reviews_en", form) || [];

  // Filter out undefined elements to prevent issues
  const customerReviews = rawCustomerReviews.filter((item: any) => item !== undefined);
  const customerReviewsEn = rawCustomerReviewsEn.filter((item: any) => item !== undefined);

  // Debug: Log when form values change - removed console.log

  // addReview and removeReview functions removed - using Form.List's add/remove functions instead


  const handleAvatarUpload = (file: any, field: "customer_reviews" | "customer_reviews_en", index: number) => {
    const formData = new FormData();
    formData.append("image", file);

    uploadPhoto(formData, {
      onSuccess: (data) => {
        // Force form to update by setting the entire array
        const currentReviews = form.getFieldValue(field) || [];
        const newReviews = [...currentReviews];
        if (newReviews[index]) {
          newReviews[index] = { ...newReviews[index], customer_avatar: data.data.path };
        } else {
          newReviews[index] = { customer_avatar: data.data.path };
        }
        form.setFieldValue(field, newReviews);
      },
      onError: (error) => {
        console.error("Upload failed:", error);
      },
    });
  };

  const handleSelectAlbum = (field: "customer_reviews" | "customer_reviews_en", index: number) => {
    setCurrentField(field);
    setCurrentReviewIndex(index);
    setIsAlbumModalOpen(true);
  };

  const handleAlbumSelected = (album: any) => {
    if (currentField && currentReviewIndex !== null) {
      // Force form to update by setting the entire array
      const currentReviews = form.getFieldValue(currentField) || [];
      const newReviews = [...currentReviews];
      if (newReviews[currentReviewIndex]) {
        newReviews[currentReviewIndex] = { ...newReviews[currentReviewIndex], album_id: album.id };
      } else {
        newReviews[currentReviewIndex] = { album_id: album.id };
      }
      form.setFieldValue(currentField, newReviews);
    }
    setIsAlbumModalOpen(false);
    setCurrentField(null);
    setCurrentReviewIndex(null);
  };

  const getSelectedAlbum = (albumId: number | string, review: any) => {
    // First try to get album from review data (from API response)
    if (review?.album) {
      return review.album;
    }
    // Fallback to selectedAlbums (for form editing)
    return selectedAlbums.find(album => album.id === albumId);
  };

  const renderReviewForm = (
    reviews: any[],
    field: "customer_reviews" | "customer_reviews_en"
  ) => (
    <div style={{ marginBottom: 16 }}>
      <Form.List name={field}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => (
              <Card
                key={key}
                size="small"
                style={{ marginBottom: 16, border: "1px solid #d9d9d9" }}
                title={
                  <Space>
                    <UserOutlined />
                    {reviews[name]?.customer_name || t("formSetup.customerReviews.reviewTitle", { index: name + 1 })}
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        remove(name);
                      }}
                    />
                  </Space>
                }
              >
                <div className="space-y-4">
                  {/* Row 1: Customer Name */}
                  <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
                    <label className="sm:w-36 text-left w-full flex gap-2 items-center text-sm font-medium text-gray-700">{t("formSetup.customerReviews.customerName")} *</label>
                    <Form.Item
                      {...restField}
                      name={[name, "customer_name"]}
                      rules={[{ required: true, message: t("formSetup.customerReviews.customerNameRequired") }]}
                      className="mb-0 flex-1"
                    >
                      <Input placeholder={t("formSetup.customerReviews.customerNamePlaceholder")} />
                    </Form.Item>
                  </div>

                  {/* Row 2: Avatar */}
                  <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
                    <label className="sm:w-36 text-left w-full flex gap-2 items-center text-sm font-medium text-gray-700">{t("formSetup.customerReviews.customerAvatar")} *</label>
                    <Form.Item
                      {...restField}
                      name={[name, "customer_avatar"]}
                      rules={[{ required: true, message: t("formSetup.customerReviews.customerAvatarRequired") }]}
                      className="mb-0 flex-1"
                    >
                        <div className="upload-container">
                          <Upload
                            listType="picture-card"
                            showUploadList={false}
                            customRequest={({ file }) => {
                              handleAvatarUpload(file, field, name);
                            }}
                            maxCount={1}
                            disabled={uploading}
                            className="custom-upload"
                          >
                            {reviews[name]?.customer_avatar ? (
                              <div className="relative w-full h-full">
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Force form to update by setting the entire array
                                    const currentReviews = form.getFieldValue(field) || [];
                                    const newReviews = [...currentReviews];
                                    if (newReviews[name]) {
                                      newReviews[name] = { ...newReviews[name], customer_avatar: "" };
                                    }
                                    form.setFieldValue(field, newReviews);
                                  }}
                                  className="absolute -top-1 -right-1 cursor-pointer flex justify-center items-center w-5 h-5 rounded-full bg-red-500 text-white text-xs hover:bg-red-600 transition-colors z-10"
                                  title={t("formSetup.customerReviews.deleteAvatar")}
                                >
                                  ×
                                </div>
                                <img
                                  src={reviews[name].customer_avatar}
                                  alt="uploaded avatar"
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              </div>
                            ) : (
                              <div className="upload-content">
                                <PlusOutlined className="upload-icon" />
                                <div className="upload-text">{uploading ? t("formSetup.customerReviews.uploading") : t("formSetup.customerReviews.upload")}</div>
                              </div>
                            )}
                          </Upload>
                        </div>
                    </Form.Item>
                  </div>

                  {/* Row 3: Album */}
                  <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
                    <label className="sm:w-36 text-left w-full flex gap-2 items-center text-sm font-medium text-gray-700">{t("formSetup.customerReviews.relatedAlbum")} *</label>
                    <Form.Item
                      {...restField}
                      name={[name, "album_id"]}
                      rules={[{ required: true, message: t("formSetup.customerReviews.relatedAlbumRequired") }]}
                      className="mb-0 flex-1"
                    >
                    <div className="flex flex-col gap-2">
                      {reviews[name]?.album_id ? (
                        <div className="flex flex-col gap-2">
                          <div className="relative w-[100px] h-[100px]">
                            <img
                              src={getSelectedAlbum(reviews[name].album_id, reviews[name])?.thumbnail_url}
                              alt="selected album"
                              className="w-full h-full object-cover rounded-lg"
                            />
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                // Force form to update by setting the entire array
                                const currentReviews = form.getFieldValue(field) || [];
                                const newReviews = [...currentReviews];
                                if (newReviews[name]) {
                                  newReviews[name] = { ...newReviews[name], album_id: undefined };
                                }
                                form.setFieldValue(field, newReviews);
                                // Force re-render by updating the form
                                form.validateFields([field]);
                              }}
                              className="absolute -top-1 -right-1 cursor-pointer flex justify-center items-center w-5 h-5 rounded-full bg-red-500 text-white text-xs hover:bg-red-600 transition-colors z-10"
                              title={t("formSetup.customerReviews.deleteAlbum")}
                            >
                              ×
                            </div>
                          </div>
                          <span className="text-xs text-gray-600 text-center max-w-20 truncate">
                            {getSelectedAlbum(reviews[name].album_id, reviews[name])?.name}
                          </span>
                        </div>
                      ) : (
                        <div
                          onClick={() => handleSelectAlbum(field, name)}
                          className="w-[100px] h-[100px] bg-gray-50 border-[1px] border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-green-500 transition-colors dark:bg-transparent"
                        >
                          <PlusOutlined className="text-gray-400 text-lg mb-1" />
                          <span className="text-xs text-gray-500">{t("formSetup.customerReviews.album")}</span>
                        </div>
                      )}
                    </div>
                    </Form.Item>
                  </div>

                  {/* Row 4: Review Content */}
                  <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-start w-full gap-2 md:gap-6 lg:gap-8">
                    <label className="sm:w-36 text-left w-full flex gap-2 items-center text-sm font-medium text-gray-700">{t("formSetup.customerReviews.reviewContent")} *</label>
                    <Form.Item
                      {...restField}
                      name={[name, "review_content"]}
                      rules={[{ required: true, message: t("formSetup.customerReviews.reviewContentRequired") }]}
                      className="mb-0 flex-1"
                    >
                    <TextArea
                      rows={4}
                      placeholder={t("formSetup.customerReviews.reviewContentPlaceholder")}
                      maxLength={1000}
                      showCount
                    />
                    </Form.Item>
                  </div>

                  {/* Row 5: Rating */}
                  <div className="flex flex-col md:flex-row justify-between sm:justify-start md:items-center w-full gap-2 md:gap-6 lg:gap-8">
                    <label className="sm:w-36 text-left w-full flex gap-2 items-center text-sm font-medium text-gray-700">{t("formSetup.customerReviews.rating")} *</label>
                    <Form.Item
                      {...restField}
                      name={[name, "rating"]}
                      rules={[{ required: true, message: t("formSetup.customerReviews.ratingRequired") }]}
                      className="mb-0 flex-1"
                    >
                      <Rate
                        style={{
                          color: '#faad14',
                          '--ant-rate-star-color': '#faad14',
                          '--ant-rate-star-bg': '#d1d5db'
                        } as React.CSSProperties}
                        className="[&_.ant-rate-star]:text-yellow-500 [&_.ant-rate-star-zero]:!text-gray-500 dark:[&_.ant-rate-star-zero]:!text-gray-400"
                      />
                    </Form.Item>
                  </div>

                </div>
              </Card>
            ))}

            <Button
              type="dashed"
              onClick={() => {
                add();
              }}
              block
              icon={<PlusOutlined />}
            >
              {t("formSetup.customerReviews.addReview")}
            </Button>
          </>
        )}
      </Form.List>
    </div>
  );

  const tabItems = [
    {
      key: "vi",
      label: t("formSetup.customerReviews.tabVietnamese"),
      children: renderReviewForm(customerReviews, "customer_reviews")
    },
    {
      key: "en",
      label: t("formSetup.customerReviews.tabEnglish"),
      children: renderReviewForm(customerReviewsEn, "customer_reviews_en")
    }
  ];

  return (
    <div>
          <style dangerouslySetInnerHTML={{
            __html: `
              .upload-container {
                display: inline-block;
              }

            .custom-upload .ant-upload-select-picture-card {
              width: 112px !important;
              height: 112px !important;
            border-radius: 8px !important;
            border: 2px dashed #d9d9d9 !important;
            background: #fafafa !important;
            display: flex !important;
            flex-direction: column !important;
            align-items: center !important;
            justify-content: center !important;
            cursor: pointer !important;
            transition: all 0.3s !important;
          }

            .custom-upload .ant-upload-select-picture-card:hover {
              border-color: #1890ff !important;
              background: #f0f8ff !important;
            }

            .upload-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              text-align: center;
            }

            .upload-icon {
              font-size: 18px;
              color: #999;
              margin-bottom: 4px;
            }

            .upload-text {
              font-size: 12px;
              color: #666;
              margin: 0;
            }

            .custom-upload .ant-upload-list-picture-card .ant-upload-list-item {
              width: 112px !important;
              height: 112px !important;
            border-radius: 8px !important;
          }

          /* Fix star rating colors for dark theme */
          .ant-rate .ant-rate-star-zero,
          .ant-rate-star.ant-rate-star-zero {
            color: #9ca3af !important;
            fill: #9ca3af !important;
          }

          .dark .ant-rate .ant-rate-star-zero,
          .dark .ant-rate-star.ant-rate-star-zero {
            color: #d1d5db !important;
            fill: #d1d5db !important;
          }

          /* Target SVG elements inside stars */
          .ant-rate .ant-rate-star-zero svg,
          .ant-rate .ant-rate-star-zero path,
          .ant-rate .ant-rate-star-zero polygon {
            fill: #9ca3af !important;
            color: #9ca3af !important;
          }

          .dark .ant-rate .ant-rate-star-zero svg,
          .dark .ant-rate .ant-rate-star-zero path,
          .dark .ant-rate .ant-rate-star-zero polygon {
            fill: #d1d5db !important;
            color: #d1d5db !important;
          }

          /* More specific selectors for customer reviews */
          .customer-reviews-tabs .ant-rate .ant-rate-star-zero svg,
          .customer-reviews-tabs .ant-rate .ant-rate-star-zero path,
          .customer-reviews-tabs .ant-rate .ant-rate-star-zero polygon {
            fill: #9ca3af !important;
            color: #9ca3af !important;
          }

          .dark .customer-reviews-tabs .ant-rate .ant-rate-star-zero svg,
          .dark .customer-reviews-tabs .ant-rate .ant-rate-star-zero path,
          .dark .customer-reviews-tabs .ant-rate .ant-rate-star-zero polygon {
            fill: #d1d5db !important;
            color: #d1d5db !important;
          }
        `
      }} />

      <Tabs
        defaultActiveKey="vi"
        items={tabItems}
        className="customer-reviews-tabs"
      />

      <Modal
        title={t("formSetup.customerReviews.selectAlbum")}
        open={isAlbumModalOpen}
        onCancel={() => {
          setIsAlbumModalOpen(false);
          setCurrentField(null);
          setCurrentReviewIndex(null);
        }}
        width={1000}
        footer={null}
        centered
        destroyOnClose
      >
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 max-h-[500px] overflow-y-auto p-2">
          {selectedAlbums?.map((album: any) => (
            <div
              key={album.id}
              onClick={() => handleAlbumSelected(album)}
              className="relative cursor-pointer group hover:scale-105 transition-transform"
            >
              <img
                src={album.thumbnail_url}
                alt={album.name}
                className="w-full h-40 object-cover rounded-lg shadow-md"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all rounded-lg flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium text-center px-2">
                  {album.name}
                </div>
              </div>
            </div>
          ))}
        </div>
        {selectedAlbums?.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-lg mb-2">{t("formSetup.customerReviews.noAlbumsSelected")}</div>
            <div className="text-sm">{t("formSetup.customerReviews.noAlbumsMessage")}</div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerReviews;
